import React, { useState } from 'react';
import { IkigaiBuckets, CareerPath } from '../types';
import { Heart, Globe, Briefcase, Zap, AlertTriangle, CheckCircle2, Circle, Target, Ban, ChevronDown, Radar, Sparkles } from 'lucide-react';
import { AlignmentCompass } from './AlignmentCompass';
import { useTheme, BucketStyle } from '../contexts/ThemeContext';

interface Props {
    data: IkigaiBuckets;
    careerPaths: CareerPath[];
    onSynthesize: () => void;
    isSynthesizing: boolean;
    currentQuest: string | null;
    isQuestCompleted: boolean;
    onToggleQuest: () => void;
    onViewSkillTree: (careerTitle: string) => void;
    isGeneratingTree: boolean;
    onVisualize: (career: CareerPath) => void;
    visualizingCareer: string | null;
    onFindHubs: (careerTitle: string) => void;
    isFindingHubs: string | null;
    onBridgeGap: (careerTitle: string) => void;
    isBridgingGap: string | null;
}

type BucketType = 'passion' | 'mission' | 'profession' | 'vocation' | 'drains';

// Helper to determine title for the Mobile Header
const getArchetypeLabel = (data: IkigaiBuckets) => {
    const totalItems = data.passion.length + data.mission.length + data.profession.length + data.vocation.length;
    if (totalItems === 0) return "Awaiting Input";
    if (totalItems < 5) return "Calibrating...";
    
    // Simple logic (mirrors the Compass logic loosely for the header label)
    const p = data.passion.length;
    const m = data.mission.length;
    const v = data.vocation.length;
    const pr = data.profession.length;

    if (p > 2 && m > 2 && v > 2 && pr > 2) return "Ikigai Aligned";
    if (p > m && p > v) return "The Dreamer";
    if (v > p && v > m) return "The Provider";
    if (pr > p) return "The Hustler";
    return "The Seeker";
};

// --- GRAVITY WELL BUCKET CARD ---
const BucketCard: React.FC<{
    id?: string;
    title: string;
    subtitle?: string;
    items: string[];
    icon: React.ReactNode;
    themeStyle?: BucketStyle;
    variant: BucketType;
    className?: string;
}> = ({ id, title, subtitle, items, icon, themeStyle, variant, className = "" }) => {
    const { theme } = useTheme();
    const hasItems = items.length > 0;

    // --- Layout Logic ---
    const isFoundation = variant === 'drains';

    // Standard Cards Logic
    const isVerticalInverted = variant === 'passion' || variant === 'mission';
    const isLeftSided = variant === 'passion' || variant === 'profession';

    const tints: Record<BucketType, string> = {
        passion: 'bg-[radial-gradient(circle_at_bottom_right,_var(--tw-gradient-stops))] from-rose-500/10 via-rose-500/5 to-transparent border-rose-200/20',
        mission: 'bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-purple-500/10 via-purple-500/5 to-transparent border-purple-200/20',
        profession: 'bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-sky-500/10 via-sky-500/5 to-transparent border-sky-200/20',
        vocation: 'bg-[radial-gradient(circle_at_top_left,_var(--tw-gradient-stops))] from-emerald-500/10 via-emerald-500/5 to-transparent border-emerald-200/20',
        drains: '' // Custom handling for drains
    };

    // Ghost Prompts for Empty States
    const ghostPrompts: Record<BucketType, string> = {
        passion: "",
        mission: "",
        profession: "",
        vocation: "",
        drains: "System Clear. No drag coefficients detected."
    };

    // --- RENDER: THE FOUNDATION CARD (Hate It) ---
    if (isFoundation) {
        return (
            <div id={id} className={`flex flex-col md:flex-row relative overflow-hidden group rounded-[24px] border border-dashed transition-all duration-500 ${className} ${theme.mode === 'dark' ? 'bg-red-950/10 border-red-900/40 hover:bg-red-950/20' : 'bg-red-50/50 border-red-200/60 hover:bg-red-50'}`}>
                
                {/* Hazard Stripes Background */}
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #000, #000 10px, transparent 10px, transparent 20px)' }}></div>

                {/* Left: The Warning Label */}
                <div className={`w-full md:w-48 shrink-0 flex flex-row md:flex-col items-center md:justify-center p-4 gap-3 md:gap-2 border-b md:border-b-0 md:border-r ${theme.mode === 'dark' ? 'border-red-900/30 bg-red-950/20' : 'border-red-100 bg-red-100/30'}`}>
                    <div className={`p-2 rounded-lg ${theme.mode === 'dark' ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-500'} ring-1 ring-red-500/20 shadow-inner`}>
                        <AlertTriangle size={20} className="animate-pulse-slow" />
                    </div>
                    <div className="text-left md:text-center">
                        <h3 className={`font-black text-sm uppercase tracking-widest ${theme.mode === 'dark' ? 'text-red-200' : 'text-red-800'}`}>
                            {title}
                        </h3>
                        <p className={`text-[9px] font-mono font-bold uppercase opacity-60 ${theme.colors.text.secondary}`}>
                            {subtitle}
                        </p>
                    </div>
                    {items.length > 0 && (
                        <div className={`ml-auto md:ml-0 text-[10px] font-black px-2 py-0.5 rounded border ${theme.mode === 'dark' ? 'bg-red-950 text-red-400 border-red-900' : 'bg-red-100 text-red-600 border-red-200'}`}>
                            {items.length} THREATS
                        </div>
                    )}
                </div>

                {/* Right: The Containment Grid */}
                <div className="flex-1 p-4 md:p-5 relative z-10">
                    {items.length === 0 ? (
                        <div className="h-full flex items-center justify-center md:justify-start opacity-40 gap-2">
                            <CheckCircle2 size={16} className="text-green-500" />
                            <p className="text-xs font-mono font-medium uppercase tracking-wide">{ghostPrompts.drains}</p>
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {items.map((item, idx) => (
                                <div key={idx} className={`group/chip relative pl-2 pr-2 py-1.5 rounded flex items-center gap-2 border transition-all duration-300 hover:pr-8 ${theme.mode === 'dark' 
                                    ? 'bg-red-950/40 border-red-900/50 text-red-200 hover:border-red-500 hover:bg-red-900/60' 
                                    : 'bg-white border-red-100 text-red-800 hover:border-red-300 shadow-sm'
                                }`}>
                                    <Ban size={12} className="opacity-50 shrink-0" />
                                    <span className="text-xs font-mono font-bold">{item}</span>
                                    
                                    {/* Hover "Delete/Neutralize" Visual */}
                                    <div className="absolute right-2 opacity-0 group-hover/chip:opacity-100 transition-opacity">
                                        <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-ping"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // --- RENDER: STANDARD CARDS (Positive Buckets) ---
    const baseGlass = `${tints[variant]} shadow-lg`;
    const containerStyle = `${baseGlass} backdrop-blur-xl border transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:border-opacity-50`;
    const animation = !hasItems ? 'animate-pulse-slow' : '';

    const IconBox = (
        <div className={`p-3 rounded-2xl transition-all duration-500 shadow-inner shrink-0 ${
            'bg-white/10 dark:bg-slate-900/20 backdrop-blur-md ' + (themeStyle?.icon || 'text-slate-400')
            }`}>
            {icon}
        </div>
    );

    const TitleBox = (
        <div className={!isFoundation && isLeftSided ? 'text-right' : 'text-left'}>
            <h3 className={`font-bold text-lg tracking-tight transition-colors duration-500 leading-tight ${theme.colors.text.main}`}>
                {title}
            </h3>
            {subtitle && (
                <p className={`text-[10px] font-bold uppercase tracking-wider opacity-60 ${theme.colors.text.secondary}`}>
                    {subtitle}
                </p>
            )}
        </div>
    );

    const CountBadge = (
        <div className={`text-[10px] font-black w-6 h-6 flex items-center justify-center rounded-full backdrop-blur-md border shrink-0 ${
            'bg-white/10 dark:bg-black/10 ' + (themeStyle?.text || 'text-slate-500') + ' border-white/10'
            }`}>
            {items.length}
        </div>
    );

    const HeaderNode = (
        <div className={`flex items-center gap-3 relative z-10 
            ${!isFoundation && isVerticalInverted ? 'mt-4 pt-2' : 'mb-4'} 
            ${!isFoundation && isLeftSided ? 'justify-end' : 'justify-start'}
        `}>
            {isLeftSided ? (
                <>
                    {TitleBox}
                    {CountBadge}
                    {IconBox}
                </>
            ) : (
                <>
                    {IconBox}
                    {CountBadge}
                    {TitleBox}
                </>
            )}
        </div>
    );

    const ContentNode = (
        <div className={`flex-1 relative z-10 overflow-y-auto custom-scrollbar flex flex-col 
            ${isVerticalInverted ? 'justify-end' : 'justify-start'}
        `}>
            {items.length === 0 ? (
                <div className={`h-full flex flex-col opacity-60 
                    ${isVerticalInverted ? 'justify-end' : 'justify-start'}
                    ${!isFoundation && isLeftSided ? 'items-end pr-2 text-right' : 'items-start pl-2 text-left'}
                `}>
                    <p className={`text-sm font-medium leading-snug ${theme.colors.text.secondary}`}>
                        {ghostPrompts[variant]}
                    </p>
                </div>
            ) : (
                <ul className="space-y-2">
                    {items.map((item, idx) => (
                        <li key={idx} className={`flex items-center animate-fade-in-up group/item 
                            ${!isFoundation && isLeftSided ? 'justify-end' : 'justify-start'}
                        `} style={{ animationDelay: `${idx * 50}ms` }}>
                            {!isLeftSided && <span className={`w-1.5 h-1.5 rounded-full mr-2 opacity-50 shrink-0 ${themeStyle?.indicator || 'bg-slate-400'}`}></span>}
                            <span className={`text-sm font-medium leading-relaxed truncate transition-colors ${theme.colors.text.main} ${!isFoundation && isLeftSided ? 'text-right' : 'text-left'}`}>{item}</span>
                            {isLeftSided && <span className={`w-1.5 h-1.5 rounded-full ml-3 opacity-50 shrink-0 ${themeStyle?.indicator}`}></span>}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );

    return (
        <div id={id} className={`flex relative overflow-hidden group ${containerStyle} ${animation} ${className} flex-col p-6 min-h-[220px] rounded-[32px]`}>
            {isVerticalInverted ? ContentNode : HeaderNode}
            {isVerticalInverted ? HeaderNode : ContentNode}
        </div>
    );
};

export const IkigaiDashboard: React.FC<Props> = ({
    data,
    currentQuest,
    isQuestCompleted,
    onToggleQuest
}) => {
    const { theme } = useTheme();
    const [activeMobileTab, setActiveMobileTab] = useState<BucketType>('passion');
    const [isRadarOpen, setIsRadarOpen] = useState(false); // Collapsed HUD state

    // Mobile Tab Config
    const tabs: { id: BucketType, icon: React.ReactNode, label: string, colorClass: string }[] = [
        { id: 'passion', icon: <Heart size={14} />, label: 'Love', colorClass: 'bg-rose-500 text-white' },
        { id: 'mission', icon: <Globe size={14} />, label: 'Need', colorClass: 'bg-purple-500 text-white' },
        { id: 'profession', icon: <Zap size={14} />, label: 'Good', colorClass: 'bg-sky-500 text-white' },
        { id: 'vocation', icon: <Briefcase size={14} />, label: 'Paid', colorClass: 'bg-emerald-500 text-white' }
    ];

    return (
        <div className="relative h-full flex flex-col p-4 md:p-6 transition-colors duration-700 overflow-hidden">

            {/* 0. Orbital Atmosphere */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-[-10%] right-[-10%] w-[600px] h-[600px] bg-indigo-500/5 rounded-full blur-[120px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-[120px]"></div>
            </div>

            {/* 1. Micro Quest (Top Row now) */}
            {currentQuest && (
                <div className="relative z-20 shrink-0 mb-4 animate-fade-in-up">
                    <div className={`flex items-center h-12 gap-3 px-5 rounded-full border shadow-lg backdrop-blur-xl w-full bg-white/90 dark:bg-slate-900/90 border-indigo-100 dark:border-indigo-900/50 shadow-indigo-500/10`}>
                        <div className={`p-1.5 rounded-full shrink-0 ${isQuestCompleted ? 'bg-green-100 text-green-600' : 'bg-indigo-100 text-indigo-600'}`}>
                            <Target size={14} />
                        </div>
                        <div className="flex flex-col min-w-0 flex-1">
                            <span className="text-[9px] uppercase font-bold tracking-wider opacity-50 whitespace-nowrap">Active Objective</span>
                            <span className={`text-xs font-bold truncate ${isQuestCompleted ? 'line-through opacity-50' : ''} ${theme.colors.text.main}`} title={currentQuest}>
                                {currentQuest}
                            </span>
                        </div>
                        <button
                            onClick={onToggleQuest}
                            className={`ml-2 p-1 rounded-full shrink-0 transition-all hover:scale-110 ${isQuestCompleted ? 'text-green-500' : 'text-slate-300 hover:text-green-500'}`}
                        >
                            {isQuestCompleted ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                        </button>
                    </div>
                </div>
            )}

            {/* 2. Main Convergence Grid */}
            <div className="relative z-10 flex-1 min-h-0 overflow-y-auto custom-scrollbar md:overflow-visible">
                <div className="relative w-full h-full flex flex-col gap-4">
                    
                    {/* Top Zone: The 4 Pillars */}
                    <div className="relative flex-[4] z-10 flex flex-col min-h-0">

                        {/* DESKTOP VIEW: 2x2 Grid */}
                        <div className="hidden md:grid grid-cols-2 gap-4 h-full min-h-[360px]">
                            <div className={`absolute inset-0 z-20 pointer-events-none flex justify-center items-center`}>
                                <AlignmentCompass data={data} variant="floating" className="w-[50%] h-[50%] min-w-[250px] min-h-[250px]" />
                            </div>
                            <BucketCard id="bucket-passion" className="h-full" title="Love It" subtitle="(Interest)" items={data.passion} icon={<Heart size={20} />} themeStyle={theme.colors.buckets.passion} variant="passion" />
                            <BucketCard id="bucket-mission" className="h-full" title="World Needs" subtitle="(Contribution)" items={data.mission} icon={<Globe size={20} />} themeStyle={theme.colors.buckets.mission} variant="mission" />
                            <BucketCard id="bucket-profession" className="h-full" title="Good At" subtitle="(Skill)" items={data.profession} icon={<Zap size={20} />} themeStyle={theme.colors.buckets.profession} variant="profession" />
                            <BucketCard id="bucket-vocation" className="h-full" title="Making Money" subtitle="(Market)" items={data.vocation} icon={<Briefcase size={20} />} themeStyle={theme.colors.buckets.vocation} variant="vocation" />
                        </div>

                        {/* MOBILE VIEW: Collapsible HUD + Tabs */}
                        <div className="md:hidden flex flex-col h-full gap-4 min-h-0">
                            
                            {/* THE HUD (Collapsible Radar) */}
                            <div className={`shrink-0 rounded-3xl border shadow-sm overflow-hidden transition-all duration-500 ease-in-out ${theme.mode === 'dark' ? 'bg-slate-900 border-slate-800' : 'bg-white border-white/50'}`}>
                                {/* Header Toggle */}
                                <button 
                                    onClick={() => setIsRadarOpen(!isRadarOpen)}
                                    className="w-full flex items-center justify-between p-4"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-xl ${isRadarOpen ? 'bg-indigo-500 text-white' : 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'}`}>
                                            {isRadarOpen ? <Radar size={18} className="animate-spin-slow" /> : <Sparkles size={18} />}
                                        </div>
                                        <div className="text-left">
                                            <span className={`block text-[10px] font-black uppercase tracking-widest opacity-50 ${theme.colors.text.main}`}>Analysis Status</span>
                                            <span className={`block text-sm font-bold ${theme.colors.text.main}`}>{getArchetypeLabel(data)}</span>
                                        </div>
                                    </div>
                                    <div className={`text-slate-400 transition-transform duration-300 ${isRadarOpen ? 'rotate-180' : ''}`}>
                                        <ChevronDown size={20} />
                                    </div>
                                </button>

                                {/* Expanded Content (The Radar) */}
                                <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isRadarOpen ? 'max-h-[320px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                    <div className="p-4 pt-0 flex justify-center pb-6">
                                        <AlignmentCompass data={data} className="w-full max-w-[240px] h-[240px]" />
                                    </div>
                                </div>
                            </div>

                            {/* Tabs */}
                            <div className={`shrink-0 flex p-1 rounded-2xl border backdrop-blur-md ${theme.mode === 'dark' ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
                                {tabs.map((tab) => {
                                    const isActive = activeMobileTab === tab.id;
                                    const count = data[tab.id].length;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveMobileTab(tab.id)}
                                            className={`flex-1 flex flex-col items-center justify-center py-2 rounded-xl transition-all duration-300 relative ${
                                                isActive ? `${tab.colorClass} shadow-md` : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                                            }`}
                                        >
                                            {tab.icon}
                                            <span className="text-[10px] font-bold mt-0.5">{tab.label}</span>
                                            {count > 0 && !isActive && (
                                                <span className="absolute top-1 right-2 w-1.5 h-1.5 rounded-full bg-current opacity-50"></span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Active Mobile Card */}
                            <div className="flex-1 min-h-0 relative">
                                {activeMobileTab === 'passion' && (
                                    <div className="absolute inset-0 animate-fade-in-up">
                                        <BucketCard id="bucket-passion-mobile" className="h-full" title="Love It" subtitle="(Interest)" items={data.passion} icon={<Heart size={20} />} themeStyle={theme.colors.buckets.passion} variant="passion" />
                                    </div>
                                )}
                                {activeMobileTab === 'mission' && (
                                    <div className="absolute inset-0 animate-fade-in-up">
                                        <BucketCard id="bucket-mission-mobile" className="h-full" title="World Needs" subtitle="(Contribution)" items={data.mission} icon={<Globe size={20} />} themeStyle={theme.colors.buckets.mission} variant="mission" />
                                    </div>
                                )}
                                {activeMobileTab === 'profession' && (
                                    <div className="absolute inset-0 animate-fade-in-up">
                                        <BucketCard id="bucket-profession-mobile" className="h-full" title="Good At" subtitle="(Skill)" items={data.profession} icon={<Zap size={20} />} themeStyle={theme.colors.buckets.profession} variant="profession" />
                                    </div>
                                )}
                                {activeMobileTab === 'vocation' && (
                                    <div className="absolute inset-0 animate-fade-in-up">
                                        <BucketCard id="bucket-vocation-mobile" className="h-full" title="Making Money" subtitle="(Market)" items={data.vocation} icon={<Briefcase size={20} />} themeStyle={theme.colors.buckets.vocation} variant="vocation" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Bottom Zone: The Foundation (Hate It) */}
                    <div className="flex-1 min-h-[110px] w-full z-20 shrink-0">
                        <BucketCard
                            id="bucket-drains" 
                            className="h-full w-full"
                            title="HATE-IT" subtitle="(Energy Drainers)"
                            items={data.drains} icon={<AlertTriangle size={20} />}
                            variant="drains"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};